/**
 * Utility functions for managing feedback modal display logic.
 * Server response decides eligibility; localStorage is used only for snooze state.
 * @namespace feedbackModalUtils
 */

// Base storage key for feedback modal state (user-specific suffix added below)
const FEEDBACK_MODAL_STORAGE_KEY_BASE = 'feedback_modal_last_shown';

/**
 * Feedback modal snooze duration by intent in days.
 */
const FEEDBACK_SNOOZE_DAYS_BY_INTENT = {
    skip: 60,
    x: 30,
    'outside-click': 14,
};

/**
 * Default intent used when a handler does not pass one explicitly.
 */
const DEFAULT_FEEDBACK_SNOOZE_INTENT = 'x';

/**
 * Get the user-specific storage key for tracking feedback modal display
 * @private
 * @param {number|string} userId - The user's ID
 * @returns {string} - User-specific storage key
 */
function getStorageKey(userId) {
    if (!userId) {
        // Fallback to base key if no user ID (shouldn't happen in normal flow)
        return FEEDBACK_MODAL_STORAGE_KEY_BASE;
    }

    return `${FEEDBACK_MODAL_STORAGE_KEY_BASE}_${userId}`;
}

/**
 * Get the user-specific storage key for snooze state.
 * @private
 * @param {number|string} userId - The user's ID
 * @returns {string} - User-specific snooze storage key
 */
function getSnoozeStorageKey(userId) {
    return `${getStorageKey(userId)}_snooze`;
}

/**
 * Check if a user is active and authenticated.
 * @param {Object} userStore
 * @returns {boolean}
 */
export function isActiveUser(userStore) {
    if (!userStore?.isAuthenticated || !userStore?.user) {
        return false;
    }

    return !userStore.user.deactivated_at;
}

/**
 * Main function to determine if the feedback modal should be shown.
 * Server-side `feedback_modal.should_show` is the source of truth.
 * @param {Object} userStore
 * @returns {boolean}
 */
export function shouldShowFeedbackModal(userStore) {
    if (!isActiveUser(userStore)) {
        return false;
    }

    const userId = userStore.user?.id;
    if (!userId || isFeedbackSnoozed(userId)) {
        return false;
    }

    return Boolean(userStore.user?.feedback_modal?.should_show);
}

/**
 * Check if feedback is currently snoozed for the user.
 * @private
 * @param {number|string} userId
 * @returns {boolean}
 */
function isFeedbackSnoozed(userId) {
    try {
        const storageKey = getSnoozeStorageKey(userId);
        const snoozeUntilString = localStorage.getItem(storageKey);
        
        if (!snoozeUntilString) return false;
        
        const snoozeUntil = new Date(snoozeUntilString);
        return !isNaN(snoozeUntil.getTime()) && new Date() < snoozeUntil;
    } catch (error) {
        console.warn('Error checking feedback snooze:', error);
        return false;
    }
}

/**
 * Resolve snooze days for a feedback modal dismissal intent.
 * @param {string} intent - One of `skip`, `x`, `outside-click`.
 * @returns {number} Number of days.
 */
function getSnoozeDaysByIntent(intent = DEFAULT_FEEDBACK_SNOOZE_INTENT) {
    const safeIntent = typeof intent === 'string' ? intent : DEFAULT_FEEDBACK_SNOOZE_INTENT;
    return FEEDBACK_SNOOZE_DAYS_BY_INTENT[safeIntent] || FEEDBACK_SNOOZE_DAYS_BY_INTENT.x;
}

/**
 * Snooze the feedback modal for a specified intent or number of days.
 * @param {number|string} userId - Active user identifier.
 * @param {number|string} intentOrDays - One of `skip`, `x`, `outside-click`, or a direct day count.
 */
export function setFeedbackSnooze(userId, intentOrDays = DEFAULT_FEEDBACK_SNOOZE_INTENT) {
    if (!userId) return;

    const days =
        typeof intentOrDays === 'number'
            ? intentOrDays
            : getSnoozeDaysByIntent(intentOrDays);

    try {
        const storageKey = getSnoozeStorageKey(userId);
        const snoozeUntil = new Date();
        snoozeUntil.setDate(snoozeUntil.getDate() + days);
        localStorage.setItem(storageKey, snoozeUntil.toISOString());
    } catch (error) {
        console.warn('Error setting feedback snooze:', error);
    }
}

export { FEEDBACK_SNOOZE_DAYS_BY_INTENT, DEFAULT_FEEDBACK_SNOOZE_INTENT };
